'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { toast } from '@/lib/toast'
import { navigateUrl } from '@/utils/client-request'
import { sendForgotOTP, verifyForgotOTP, resetPassword } from '@/utils/request'
import { getPasswordError } from '@/utils/auth'

type Tab = 'phone' | 'email'

export function useForgotPasswordFlow(vendor: any) {
  const router = useRouter()
  const pathname = usePathname()

  const [step, setStep] = useState(1)
  const [tab, setTab] = useState<Tab>('phone')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [otp, setOTP] = useState('')
  const [verificationId, setVerificationId] = useState('')
  const [password, setPassword] = useState('')
  const [passwordRep, setPasswordRep] = useState('')

  const [loading, setLoading] = useState(false)
  const [isOTPLoading, setOTPLoading] = useState(false)
  const [sendOTPLoading, setSendOTPLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [sentOTP, setSentOTP] = useState(false)
  const [isCooldownActive, setIsCooldownActive] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  const identifier = tab === 'email' ? email : phone

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isCooldownActive && cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            setIsCooldownActive(false)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => { if (interval) clearInterval(interval) }
  }, [isCooldownActive, cooldownSeconds])

  const handleOTP = async () => {
    if (!identifier) return toast.error(tab === 'email' ? 'Имэйл хаяг хоосон байна' : 'Утасны дугаар хоосон байна')
    if (!otp) return toast.error('Баталгаажуулах код хоосон байна')
    setOTPLoading(true)
    const response = await verifyForgotOTP({ code: otp, email: tab === 'email' ? email : undefined, phone: tab === 'phone' ? phone : undefined, vendorId: vendor?.id })
    setOTPLoading(false)
    if (response?.error) return toast.error(response.error)
    toast.success('Амжилттай баталгаажлаа 🥳')
    setVerificationId(response.data.id)
    setStep(3)
  }

  useEffect(() => {
    if (otp && otp.length === 6) handleOTP()
  }, [otp])

  const sendOTP = async () => {
    if (!identifier) return toast.error(tab === 'email' ? 'Имэйл хаяг хоосон байна' : 'Утасны дугаар хоосон байна')
    setSendOTPLoading(true)
    const response = await sendForgotOTP({ email: tab === 'email' ? email : undefined, phone: tab === 'phone' ? phone : undefined, type: tab, vendorId: vendor?.id })
    setSendOTPLoading(false)
    if (response?.error) return toast.error(response.error)
    toast.success('Код амжилттай илгээгдлээ ✉️')
    setStep(2)
    setSentOTP(true)
    setCooldownSeconds(120)
    setIsCooldownActive(true)
  }

  const resendOTP = async () => {
    if (isCooldownActive) return
    setOTP('')
    setResendLoading(true)
    const response = await sendForgotOTP({ email: tab === 'email' ? email : undefined, phone: tab === 'phone' ? phone : undefined, type: tab, vendorId: vendor?.id })
    setResendLoading(false)
    if (response?.error) return toast.error(response.error)
    toast.success('Код дахин илгээгдлээ ✉️')
    setSentOTP(true)
    setStep(2)
    setCooldownSeconds(120)
    setIsCooldownActive(true)
  }

  const handleResetPassword = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.()
    if (!verificationId) return toast.error('Дугаар эсвэл имэйл баталгаажуулаагүй байна')
    const err = getPasswordError(password, passwordRep)
    if (err) return toast.error(err)
    setLoading(true)
    const response = await resetPassword({ email: tab === 'email' ? email : undefined, phone: tab === 'phone' ? phone : undefined, password, verificationId, vendorId: vendor?.id })
    if (!response) {
      setLoading(false)
      return toast.error('Сервертэй холбогдоход алдаа гарлаа')
    }
    if (response.error) {
      setLoading(false)
      return toast.error(response.error)
    }
    toast.success('Амжилттай нууц үг солигдлоо 🥳')
    setTimeout(() => router.push(navigateUrl('/login', pathname)), 100)
  }

  const reset = () => {
    setStep(1)
    setEmail('')
    setOTP('')
    setPhone('')
    setCooldownSeconds(0)
    setIsCooldownActive(false)
  }

  return {
    step, tab, setTab,
    email, setEmail, phone, setPhone,
    otp, setOTP, identifier,
    password, setPassword, passwordRep, setPasswordRep,
    loading, isOTPLoading, sendOTPLoading, resendLoading,
    sentOTP, verificationId,
    isCooldownActive, cooldownSeconds,
    sendOTP, resendOTP, handleOTP, handleResetPassword, reset,
  }
}
